/**
 * Strings for printed documents.
 *
 * Deliberately separate from the UI catalogue in `de.ts`/`en.ts`: the document
 * language follows the **guest**, the UI language follows the **landlord**. An
 * Austrian host serving an Irish guest reads a German screen and sends an English
 * invoice. Coupling the two is a mistake that has already been made once in the
 * AUVISO CRM, where document language and UI language had to be pulled apart again.
 */

import type { DocLang } from '../lib/documents'
import { kalendertag } from '../lib/dates'

const doc = {
  de: {
    quote: 'Angebot',
    invoice: 'Rechnung',
    credit_note: 'Gutschrift (Storno)',
    number: 'Nummer',
    issueDate: 'Datum',
    dueDate: 'Zahlbar bis',
    validUntil: 'Gültig bis',
    vatId: 'UID',
    stay: 'Aufenthalt',
    unit: 'Einheit',
    arrival: 'Anreise',
    departure: 'Abreise',
    nights: 'Nächte',
    guests: 'Personen',
    adults: 'Erwachsene',
    children: 'Kinder',
    position: 'Leistung',
    qty: 'Menge',
    unitPrice: 'Einzelpreis',
    net: 'Netto',
    vat: 'USt.',
    gross: 'Brutto',
    vatSummary: 'Steueraufstellung',
    rate: 'Satz',
    totalNet: 'Summe netto',
    totalVat: 'Umsatzsteuer',
    totalGross: 'Gesamtbetrag',
    payment: 'Zahlung',
    prepayment: 'Anzahlung',
    prepaymentDue: 'Anzahlung fällig',
    prepaymentReceived: 'Bereits geleistete Anzahlung',
    paidInFull: 'Betrag dankend erhalten',
    payMethod_cash: 'bar',
    payMethod_card: 'mit Karte',
    payMethod_bank_transfer: 'per Überweisung',
    payMethod_stripe: 'online über Stripe',
    payMethod_paypal: 'über PayPal',
    payMethod_other: '',
    deposit: 'Kaution',
    depositNote:
      'Die Kaution wird gesondert eingehoben und nach der Abreise abzüglich etwaiger Schäden zurückgezahlt. Sie ist nicht Teil des Rechnungsbetrags.',
    paid: 'Bereits bezahlt',
    outstanding: 'Offener Betrag',
    bankTransfer: 'Bitte überweisen Sie den Betrag auf folgendes Konto:',
    accountHolder: 'Kontoinhaber',
    reference: 'Verwendungszweck',
    smallBusiness:
      'Kleinunternehmer gemäß § 6 Abs. 1 Z 27 UStG — es wird keine Umsatzsteuer ausgewiesen.',
    quoteIntro: 'Vielen Dank für Ihre Anfrage. Gerne bieten wir Ihnen an:',
    quoteTerms:
      'Dieses Angebot ist bis zum genannten Datum gültig. Die Buchung wird mit Eingang der Anzahlung verbindlich.',
    invoiceThanks: 'Vielen Dank für Ihren Aufenthalt.',
    page: 'Seite',
  },
  en: {
    quote: 'Quote',
    invoice: 'Invoice',
    credit_note: 'Credit note',
    number: 'Number',
    issueDate: 'Date',
    dueDate: 'Payable by',
    validUntil: 'Valid until',
    vatId: 'VAT ID',
    stay: 'Stay',
    unit: 'Unit',
    arrival: 'Arrival',
    departure: 'Departure',
    nights: 'nights',
    guests: 'Guests',
    adults: 'Adults',
    children: 'Children',
    position: 'Description',
    qty: 'Qty',
    unitPrice: 'Unit price',
    net: 'Net',
    vat: 'VAT',
    gross: 'Gross',
    vatSummary: 'Tax summary',
    rate: 'Rate',
    totalNet: 'Total net',
    totalVat: 'VAT',
    totalGross: 'Total',
    payment: 'Payment',
    prepayment: 'Deposit payment',
    prepaymentDue: 'Deposit due',
    prepaymentReceived: 'Deposit already paid',
    paidInFull: 'Payment received with thanks',
    payMethod_cash: 'in cash',
    payMethod_card: 'by card',
    payMethod_bank_transfer: 'by bank transfer',
    payMethod_stripe: 'online via Stripe',
    payMethod_paypal: 'via PayPal',
    payMethod_other: '',
    deposit: 'Security deposit',
    depositNote:
      'The security deposit is collected separately and refunded after departure, less any damages. It is not part of the invoice total.',
    paid: 'Already paid',
    outstanding: 'Amount due',
    bankTransfer: 'Please transfer the amount to the following account:',
    accountHolder: 'Account holder',
    reference: 'Payment reference',
    smallBusiness: 'Small business scheme — no VAT is charged.',
    quoteIntro: 'Thank you for your enquiry. We are pleased to offer:',
    quoteTerms:
      'This quote is valid until the date shown. The booking becomes binding once the deposit payment is received.',
    invoiceThanks: 'Thank you for your stay.',
    page: 'Page',
  },
} satisfies Record<DocLang, Record<string, string>>

export type DocKey = keyof typeof doc.de

/** Document translation. Takes the language explicitly — never reads the UI language. */
export function docT(lang: DocLang, key: DocKey): string {
  return doc[lang][key] ?? doc.de[key] ?? key
}

/**
 * Labels for the price-engine line codes that have no `name` column of their own.
 * Fees and tourist tax carry their own bilingual jsonb name and never come here.
 */
const lineLabels: Record<DocLang, Record<string, string>> = {
  de: {
    accommodation: 'Übernachtung',
    extra_person: 'Zusatzperson',
    extra_person_child: 'Zusatzperson (Kind)',
    child_discount: 'Kinderermäßigung',
    discount: 'Rabatt',
    cleaning: 'Endreinigung',
    linen: 'Bettwäsche',
    pet: 'Haustier',
    parking: 'Stellplatz',
    tourist_tax: 'Ortstaxe',
  },
  en: {
    accommodation: 'Accommodation',
    extra_person: 'Extra person',
    extra_person_child: 'Extra person (child)',
    child_discount: 'Children’s discount',
    discount: 'Discount',
    cleaning: 'Final cleaning',
    linen: 'Linen',
    pet: 'Pet',
    parking: 'Parking',
    tourist_tax: 'Tourist tax',
  },
}

export function docLineLabel(lang: DocLang, code: string): string {
  return lineLabels[lang][code] ?? lineLabels.de[code] ?? code
}

export function docFormatMoney(lang: DocLang, amount: number, currency: string): string {
  return new Intl.NumberFormat(lang === 'de' ? 'de-AT' : 'en-GB', {
    style: 'currency', currency,
  }).format(amount)
}

export function docFormatDate(lang: DocLang, iso: string): string {
  // `kalendertag` statt `new Date(iso + 'T00:00:00')` — ein Zeitstempel an dieser
  // Stelle würde sonst das ganze Dokument in die Fehlergrenze reißen (18.08.2026,
  // Begründung in lib/dates.ts). Ein Beleg zeigt lieber ein leeres Datum als keinen
  // Beleg.
  const d = kalendertag(iso)
  if (!d) return ''
  return new Intl.DateTimeFormat(lang === 'de' ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(d)
}
